import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTeam } from '@/hooks/useTeam'
import { useTeamStats, useGlobalLeaderboard, useGlobalTeamStats, usePlayerStats, useDuoStats, useGameOfTheDay } from '@/hooks/useStats'
import { useTeamUsers } from '@/hooks/useTeamUsers'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Leaderboard } from '@/components/stats/Leaderboard'
import { RecentGames } from '@/components/stats/RecentGames'
import { PlayerStats } from '@/components/stats/PlayerStats'
import { DuoStats } from '@/components/stats/DuoStats'
import { HighlightedGame } from '@/components/stats/HighlightedGame'
import { BarChart3, Users, Trophy, TrendingUp, Globe, Target, Clock, Calendar, Infinity } from 'lucide-react'

type TimeFilter = 'all_time' | 'weekly' | 'daily'

export function Statistics() {
  const { currentTeam } = useTeam()
  const [view, setView] = useState<'team' | 'global'>('team')
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all_time')
  const { data: stats, isLoading: statsLoading } = useTeamStats(currentTeam?.id, timeFilter)
  const { data: gameOfTheDay, isLoading: gameOfTheDayLoading } = useGameOfTheDay(currentTeam?.id)
  const { data: users, isLoading: usersLoading } = useTeamUsers(currentTeam?.id)
  const { data: globalLeaderboard, isLoading: globalLeaderboardLoading } = useGlobalLeaderboard()
  const { data: globalTeams, isLoading: globalTeamsLoading } = useGlobalTeamStats()
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null)
  const [selectedDuo, setSelectedDuo] = useState<{ player1: string; player2: string } | null>(null)
  
  // Fetch player stats when a player is selected
  const { data: playerStatsData, isLoading: playerStatsLoading } = usePlayerStats(
    selectedPlayer || undefined,
    currentTeam?.id
  )
  
  // Fetch duo stats when both players are selected
  const { data: duoStatsData, isLoading: duoStatsLoading } = useDuoStats(
    selectedDuo?.player1 || undefined,
    selectedDuo?.player2 || undefined,
    currentTeam?.id
  )

  if (statsLoading || usersLoading || gameOfTheDayLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!currentTeam) {
    return (
      <div className="text-center py-12">
        <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Team</h3>
        <p className="text-gray-500">Please create an environment to view statistics.</p>
      </div>
    )
  }

  const approvedUsers = users?.filter((u: any) => u.status === 'approved') || []

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Statistics</h1>
          <p className="mt-1 text-sm text-gray-500">
            {view === 'team' 
              ? `Detailed analytics and performance metrics for ${currentTeam.name}`
              : 'Global statistics across all teams'}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setView('team')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
              view === 'team'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Target className="h-4 w-4 mr-2" />
            My Team
          </button>
          <button
            onClick={() => setView('global')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
              view === 'global'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Globe className="h-4 w-4 mr-2" />
            Global
          </button>
        </div>
      </div>

      {view === 'team' ? (
        <>
          {/* Time Filter */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700">Time Period</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => setTimeFilter('all_time')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center ${
                    timeFilter === 'all_time'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Infinity className="h-4 w-4 mr-1.5" />
                  All Time
                </button>
                <button
                  onClick={() => setTimeFilter('weekly')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center ${
                    timeFilter === 'weekly'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Calendar className="h-4 w-4 mr-1.5" />
                  This Week
                </button>
                <button
                  onClick={() => setTimeFilter('daily')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center ${
                    timeFilter === 'daily'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Clock className="h-4 w-4 mr-1.5" />
                  Today
                </button>
              </div>
            </div>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart3 className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Games</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats?.total_games || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Players</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats?.most_active_players?.length || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Trophy className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Top Player</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats?.leaderboard?.[0]?.display_name || '-'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-purple-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Recent Activity</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats?.recent_games?.length || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Game of the Day - Always shows today's game regardless of filter */}
      {gameOfTheDay && (
        <HighlightedGame game={gameOfTheDay} />
      )}

      {/* Main Statistics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leaderboard */}
        <Leaderboard 
          players={stats?.leaderboard || []} 
          title="Leaderboard"
          maxItems={10}
        />

        {/* Recent Games */}
        <RecentGames 
          games={stats?.recent_games || []} 
          title="Recent Games"
          maxItems={10}
        />
      </div>

      {/* Player Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Player Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {approvedUsers.map((user: any) => (
            <button
              key={user.id}
              onClick={() => setSelectedPlayer(selectedPlayer === user.id ? null : user.id)}
              className={`p-4 text-left border rounded-lg transition-colors ${
                selectedPlayer === user.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="font-medium text-gray-900">{user.display_name}</div>
              <div className="text-sm text-gray-500">Click to view detailed stats</div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected Player Stats */}
      {selectedPlayer && (
        playerStatsLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : playerStatsData ? (
          <PlayerStats stats={playerStatsData} />
        ) : null
      )}

      {/* Duo Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Duo Performance</h3>
        <div className="text-sm text-gray-500 mb-4">
          Select two players to see their performance together
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Player 1</label>
            <select
              value={selectedDuo?.player1 || ''}
              onChange={(e) => setSelectedDuo(prev => ({ ...prev, player1: e.target.value, player2: '' }))}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select player...</option>
              {approvedUsers.map((user: any) => (
                <option key={user.id} value={user.id}>{user.display_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Player 2</label>
            <select
              value={selectedDuo?.player2 || ''}
              onChange={(e) => setSelectedDuo(prev => ({ player1: prev?.player1 || '', player2: e.target.value }))}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              disabled={!selectedDuo?.player1}
            >
              <option value="">Select player...</option>
              {approvedUsers
                .filter((user: any) => user.id !== selectedDuo?.player1)
                .map((user: any) => (
                  <option key={user.id} value={user.id}>{user.display_name}</option>
                ))}
            </select>
          </div>
        </div>
      </div>

      {/* Selected Duo Stats */}
      {selectedDuo?.player1 && selectedDuo?.player2 && (
        duoStatsLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : duoStatsData ? (
          <DuoStats stats={duoStatsData} />
        ) : null
      )}
        </>
      ) : (
        <>
          {/* Global View */}
          {globalLeaderboardLoading || globalTeamsLoading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              {/* Global Leaderboard */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Trophy className="h-5 w-5 mr-2 text-yellow-500" />
                  Global Leaderboard
                </h3>
                <div className="space-y-3">
                  {globalLeaderboard?.map((player: any, index: number) => (
                    <div key={player.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-400 text-white' :
                          index === 1 ? 'bg-gray-300 text-white' :
                          index === 2 ? 'bg-orange-400 text-white' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <Link
                            to={`/players/${player.id}`}
                            className="font-medium text-gray-900 hover:text-blue-600"
                          >
                            {player.display_name}
                          </Link>
                          <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                            {player.teams?.map((team: any) => (
                              <span key={team.id} className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                {team.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">{player.wins} wins</div>
                        <div className="text-sm text-gray-500">{player.total_games} games • {player.total_points} pts</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team Comparison */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Team Comparison
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {globalTeams?.map((team: any) => (
                    <div key={team.id} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">{team.name}</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Members:</span>
                          <span className="font-medium text-gray-900">{team.member_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Games:</span>
                          <span className="font-medium text-gray-900">{team.total_games}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Avg Score:</span>
                          <span className="font-medium text-gray-900">{parseFloat(team.avg_total_score).toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
